import FormattedMessage from 'I18n/components/FormattedMessage';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { useLocation, useParams } from 'quartz-routing/react-router';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { Paths } from '../../constants/paths';
import { useLocationHistory } from '../../context/LocationHistoryContext';
import { useRouteReferrer } from '../../context/RouteReferrerContext';
import { useBreadcrumbs } from '../hooks/useBreadcrumbs';
import { RouteReferrerBackLink } from './RouteReferrerBackLink';
import { jsx as _jsx } from "react/jsx-runtime";
function getDefaultBackPath(pathname, productType) {
  switch (pathname) {
    case Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS:
      return Paths.SOLUTIONS_PARTNERS;
    case Paths.SEARCH_RESULTS_PRODUCTS:
      if (productType === 'APP') return Paths.APPS;
      if (productType === 'AGENT') return Paths.AGENTS;
      return Paths.HOME;
    default:
      return Paths.HOME;
  }
}
function getBreadcrumbTestId(pathKey, title) {
  if (pathKey) {
    switch (pathKey) {
      case 'HOME':
        return 'storefront-breadcrumb-marketplace';
      case 'APPS':
        return 'storefront-breadcrumb-apps';
      case 'AGENTS':
        return 'storefront-breadcrumb-breeze-agents';
      case 'SEARCH_RESULTS':
        return 'storefront-breadcrumb-all-products';
      case 'SOLUTIONS_PARTNERS':
        return 'storefront-breadcrumb-solutions';
      case 'FEATURED':
        return 'storefront-breadcrumb-collections';
      case 'FEATURED_COLLECTION':
      case 'LISTING_DETAILS':
        if (typeof title === 'string') {
          return `storefront-breadcrumb-${title.toLowerCase().replace(/\s+/g, '-')}`;
        }
        return 'storefront-breadcrumb-dynamic';
      default:
        return 'storefront-breadcrumb-unknown';
    }
  }
  return 'storefront-breadcrumb-unknown';
}
export function StorefrontBreadcrumbs({
  title,
  pathKey,
  backBreadcrumb,
  productType
}) {
  const referrer = useRouteReferrer();
  const {
    collectionSlug,
    listingSlug
  } = useParams();
  const items = useBreadcrumbs({
    title,
    pathKey,
    backBreadcrumb,
    productType,
    slug: collectionSlug || listingSlug
  });
  const {
    getBackLinkLocation
  } = useLocationHistory();
  const backLinkLocation = getBackLinkLocation();
  const {
    pathname
  } = useLocation();

  // Prioritize navigation history over the referrer. When users navigate within
  // the marketplace (e.g., from /explore to /listing/foo), the back link should
  // go to the previous page, not the original referrer. The referrer back link
  // is only shown on the landing page where there's no prior navigation history.
  if (backBreadcrumb && backLinkLocation) {
    return /*#__PURE__*/_jsx(Breadcrumbs, {
      flush: true,
      marginBlockStart: "0",
      "data-test-id": "storefront-breadcrumbs",
      children: /*#__PURE__*/_jsx(RouterLink, {
        to: backLinkLocation,
        "data-test-id": "storefront-back-breadcrumb",
        children: /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
          backBreadcrumb: true
        })
      })
    });
  }
  if (referrer) {
    return /*#__PURE__*/_jsx(Breadcrumbs, {
      flush: true,
      marginBlockStart: "0",
      "data-test-id": "storefront-breadcrumbs",
      children: /*#__PURE__*/_jsx(RouteReferrerBackLink, {
        referrer: referrer
      })
    });
  }
  return /*#__PURE__*/_jsx(Breadcrumbs, {
    flush: true,
    marginBlockStart: "0",
    "data-test-id": "storefront-breadcrumbs",
    children: backBreadcrumb ? /*#__PURE__*/_jsx(RouterLink, {
      to: backLinkLocation !== null && backLinkLocation !== void 0 ? backLinkLocation : getDefaultBackPath(pathname, productType),
      "data-test-id": "storefront-back-breadcrumb",
      children: /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
        backBreadcrumb: true
      })
    }) : items === null || items === void 0 ? void 0 : items.map((item, index) => /*#__PURE__*/_jsx("span", {
      "data-test-id": getBreadcrumbTestId(item.pathKey, item.title),
      children: /*#__PURE__*/_jsx(StorefrontBreadcrumbItem, {
        item: item
      })
    }, `${item.pathKey}-${index}`))
  });
}
function StorefrontBreadcrumbItem({
  item
}) {
  if (item.isCurrentPage) {
    return /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
      title: item.title,
      pathKey: item.pathKey
    });
  }
  if (item.pathKey) {
    return /*#__PURE__*/_jsx(RouterLink, {
      to: item.slug ? Paths[item.pathKey].replace(':slug', item.slug) : Paths[item.pathKey],
      children: /*#__PURE__*/_jsx(StorefrontBreadcrumbMessage, {
        title: item.title,
        pathKey: item.pathKey
      })
    });
  }
  return null;
}
function StorefrontBreadcrumbMessage({
  backBreadcrumb,
  pathKey,
  title
}) {
  if (backBreadcrumb) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplaceStorefront.breadcrumbs.back"
    });
  }
  switch (pathKey) {
    case 'HOME':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.breadcrumbs.marketplace"
      });
    case 'APPS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.breadcrumbs.apps"
      });
    case 'AGENTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.breadcrumbs.agents"
      });
    case 'SOLUTIONS_PARTNERS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.breadcrumbs.solutions"
      });
    case 'SEARCH_RESULTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.breadcrumbs.explore"
      });
    case 'FEATURED':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.breadcrumbs.featured"
      });
    case 'FEATURED_COLLECTION':
    case 'LISTING_DETAILS':
      return title || '';
    default:
      return '';
  }
}